import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import useAppStyles from '../../providers/Styles/hooks';
import { BackgroundType, MediaItem } from '../../types/Styles';

import styles from './styles.module.scss';

const getFileExtension = (src: string) => {
	return src?.split('.').pop()?.toLowerCase();
};

const BG_PATTER_KEY = 'background-pattern';
const IS_IN_IFRAME = window.self !== window.top;

const BackgroundPattern = () => {
	const { background, bgColor } = useAppStyles();
	const fileExtension = useMemo(() => {
		return getFileExtension((background?.value as MediaItem)?.filename);
	}, [background?.value]);
	const isImage = useMemo(() => {
		return background?.type === BackgroundType.IMAGE;
	}, [background?.type]);
	const [svgAsText, setSvgAsText] = useState<string>('');
	const backgroundColor = useMemo(() => {
		if (background?.type === BackgroundType.COLOR) return background.value as string;

		return background?.options?.backgroundColor ?? bgColor;
	}, [background]);
	const backgroundImage = useMemo(() => {
		if (fileExtension !== 'svg' && (background?.value as MediaItem)?.url) {
			return `url(${(background?.value as MediaItem)?.url})`;
		}

		if (!svgAsText) return;

		const divElement = (() => {
			const div = document.createElement('div');
			div.insertAdjacentHTML('afterbegin', svgAsText);

			return div;
		})();

		divElement?.querySelectorAll('[fill]').forEach((element: any) => {
			element.setAttribute('fill', background?.options?.color || '#000000');
		});

		divElement?.querySelectorAll('[stroke]').forEach((element: any) => {
			element.setAttribute('stroke', background?.options?.color || '#000000');
		});

		return `url("data:image/svg+xml,${divElement.innerHTML
			.replaceAll('#', '%23')
			.replaceAll('<', '%3C')
			.replaceAll('>', '%3E')
			.replace(/[\t\n]/g, '')
			.replaceAll('"', "'")}")`;
	}, [svgAsText, background?.options?.color]);

	const loadSvgAsText = async () => {
		const src = (background?.value as MediaItem)?.url;

		if (!src) return;

		try {
			const cachedSvgText = JSON.parse(localStorage.getItem(BG_PATTER_KEY) || '{}');

			if (cachedSvgText.src === src) {
				setSvgAsText(cachedSvgText.svgText);
				return;
			}
		} catch (e) {
			//
		}

		try {
			const fetchHeader = new Headers();
			fetchHeader.append('pragma', 'no-cache');
			fetchHeader.append('cache-control', 'no-cache');

			const fetchParams = {
				method: 'GET',
				headers: fetchHeader,
			};

			const response = await fetch(src, fetchParams);
			const svgText = await response.text();

			if (response.status === 200) {
				setSvgAsText(svgText);

				localStorage.setItem(BG_PATTER_KEY, JSON.stringify({ src, svgText }));
			}
		} catch (e) {
			//
		}
	};

	useEffect(() => {
		if (fileExtension === 'svg') loadSvgAsText();
	}, [background?.value]);

	if (IS_IN_IFRAME) return null;

	return (
		<div className={styles.background} style={{ backgroundColor }}>
			<div
				className={classNames([styles.backgroundPattern, isImage && styles.backgroundPatternImage])}
				style={{ backgroundImage, opacity: background?.options?.opacity ?? 1 }}></div>
		</div>
	);
};

export default BackgroundPattern;
